import { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';

import { APP_ROUTES } from 'constants/routes';
import { usePopRouterEventNotShallow } from 'features/search/hooks/usePopRouterEventNotShallow';
import Layout from 'features/layout/templates/Layout';
import { Banner, MetaTags } from 'src/common';
import { CarouselFeed } from 'src/components/CarouselFeed';
import { VacancyMiniCard } from 'src/components/Cards';
import { SearchResultsSection } from 'src/components/SearchResultsSection';
import { JobsSearchStore } from 'features/search/models/jobsSearch';
import { Job } from 'api_entities/vacancies/types';
import { City } from 'api_entities/cities/types';
import { FacetedResponse } from 'api_entities/types';

import { VacanciesAtCity } from './components/VacanciesAtCity';
import { pageConfig } from '../Vacancies/page-config';
import { Breadcrumb } from '../../components/Breadcrumbs/types';
import { useBreadcrumbs } from '../../components/Breadcrumbs/hooks';
import { StyledBodyBreadcrumbs } from '../../components/Breadcrumbs';

const { useStore, filters: jobFilters } = pageConfig['job-function-in-city'];

const isProd = process.env.NEXT_PUBLIC_APP_CONTEXT === 'production';

export type VacancyInCityByJobFunctionProps = {
  city: City;
  jobFunctionName: string;
  jobFunctionSlug: string;
  jobFunctionSearchValue: string;
  data: JobsSearchStore['data'];
  filters: JobsSearchStore['filters'];
  latestJobs?: FacetedResponse<Job>;
};

const VacancyInCityByJobFunction = observer(
  ({
    city,
    jobFunctionName,
    jobFunctionSlug,
    jobFunctionSearchValue,
    data,
    filters,
    latestJobs,
  }: VacancyInCityByJobFunctionProps) => {
    usePopRouterEventNotShallow();
    const store = useStore(data, filters);

    const customBreadcrumbs: Breadcrumb[] = useMemo(
      () => [
        { href: `/bijbaan-${city.slug}`, label: city.title },
        { href: `/bijbaan-${city.slug}/${jobFunctionSlug}`, label: jobFunctionName },
      ],
      [city.title, jobFunctionName]
    );
    const breadcrumbs = useBreadcrumbs(customBreadcrumbs);

    const renderBanner = () => (
      <Banner
        imageURL="/assets/vacancies/v-1.webp"
        imageUrlMobile="/assets/vacancies/mobile-header-cover.webp"
      />
    );

    const transformQuery = useCallback(
      (query) => {
        if (query) {
          delete query.slug;

          if (city) {
            query.city = city.title;
          }

          if (jobFunctionSearchValue) {
            query.jobFunction = jobFunctionSearchValue;
          }
        }
      },
      [city?.title, jobFunctionSearchValue]
    );

    const filterCountersData = useCallback(
      ({ searchValue }) => {
        return searchValue?.toLocaleLowerCase() !== jobFunctionSearchValue?.toLocaleLowerCase();
      },
      [jobFunctionSearchValue]
    );

    return (
      <Layout>
        <MetaTags
          title={`${jobFunctionName} Studenten Bijbanen in ${city.title} - Studenten.nl`}
          description={`Wil jij snel aan de slag? Kies een leuke ${jobFunctionName} bijbaan in ${city.title} met Studenten.nl.`}
          canonicalUrl={isProd}
        />

        <SearchResultsSection
          store={store}
          filters={jobFilters}
          mainTextContent={`# Studenten ${jobFunctionName} Bijbanen in ${city.title}`}
          renderBanner={renderBanner}
          transformQuery={transformQuery}
          breadcrumbs={breadcrumbs ? <StyledBodyBreadcrumbs items={breadcrumbs} /> : undefined}
        />

        {latestJobs ? (
          <VacanciesAtCity
            pageSlug={`bijbaan-${city.slug}`}
            title={`Leukste bijbanen in ${city.title}`}
            facets={latestJobs.totalFacets.jobFunction}
            filterCountersData={filterCountersData}
          />
        ) : null}

        {latestJobs?.data?.length ? (
          <CarouselFeed
            title={`Populaire ${jobFunctionName} vacatures in ${city.title}`}
            items={latestJobs.data}
            renderItem={(job: Job) => {
              return (
                <VacancyMiniCard
                  title={job.title}
                  img={job.img}
                  city={job.city}
                  employment={job.employment}
                  company={job?.company?.title}
                  href={`${APP_ROUTES.ALL_VACANCIES}/${job.slug}`}
                />
              );
            }}
          />
        ) : null}
      </Layout>
    );
  }
);

export default VacancyInCityByJobFunction;
